import { faCircleCheck, faPlaneArrival, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useTheme } from '@mui/system'
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Divider, Grid, Typography } from '@vapor/material'
import { LocalizedString } from 'hic-components'
import { useCallback, useState, useContext, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useHotelDate } from 'src/app/hooks/context/context.hook'
import useLocalizeString from 'src/app/hooks/current-locale/use-localized-string'
import useErrorSnackBar from 'src/app/hooks/error-snack-bar/error-snack-bar'
import useSuccessSnackBar from 'src/app/hooks/success-snack-bar/use-success-snack-bar'
import useCheckInReservedReservedResources from '../../../api/check-in.hook'
import { ReservationF } from '../../../types/reservation-detail.types'
import { ReservationContext } from '../../../context/reservation-context'
import { Context } from '../../../context/reservation-context.types'
import { validateCheckIn } from './check-in-utils'
import { ReservationCheckInValidation, ReservedResourceCheckInValidation } from './check.in.types'
import { OperationIfGranted } from 'src/app/components/authorization/operation-if-granted'
import { hicOperationsPermissions, HicOperations } from 'src/app/components/authorization/pages-permissions'
import useFormatDate from 'src/app/hooks/current-locale/use-format-date'
import { ReservedResourcePhase } from 'src/api/api.generated'

export type CheckInButtonProps = {
  readOnly: boolean
  propertyId: string
  reservation: ReservationF
  selectedReservedResources: string[]
  setLoading: (loading: boolean) => void
  isTimeResource?: boolean
}

export default function CheckInButton({ propertyId, reservation, selectedReservedResources, setLoading, readOnly, isTimeResource }: CheckInButtonProps) {
  const { t } = useTranslation()
  const hotelDate = useHotelDate()
  const [validation, setValidation] = useState<ReservationCheckInValidation | null>(null)
  const showError = useErrorSnackBar()
  const showSuccess = useSuccessSnackBar()
  const checkInApi = useCheckInReservedReservedResources(propertyId)

  const selectedRRAreTimeResourceType = useMemo(() => {
    return selectedReservedResources.every((selectedRRId) =>
      reservation?.reservedResources?.find(
        (rr) =>
          rr.id === selectedRRId &&
          rr.resourceOccupancies &&
          rr.resourceOccupancies.length > 0 &&
          rr.resourceOccupancies[0].reservedResourceType.timeConfiguration &&
          rr.phase === ReservedResourcePhase.RESERVATION,
      ),
    )
  }, [isTimeResource, selectedReservedResources, reservation])

  const context = useContext(ReservationContext)

  const checkIn = useCallback(
    async (v: ReservationCheckInValidation) => {
      setLoading(true)
      const result = await checkInApi({
        reservationId: reservation.id,
        reservedResources: v.reservedResources
          .filter((r) => !hasErrors(r))
          .map((r) => ({
            reservedResourceId: r.reservedResourceId,
            guestIds: r.checkingInGuestIds,
          })),
      })
      if (result.type === 'FAIL') {
        showError(result.error)
      } else {
        showSuccess(t('pms.booking.reservation.check_in.dialog_message.check_in_success'))
      }
      setLoading(false)
    },
    [checkInApi, setLoading, t],
  )

  const onClick = useCallback(
    async (context: Context) => {
      const v = validateCheckIn(reservation, hotelDate, context, selectedReservedResources)
      if (v.error.noResources || v.reservedResources.some((r) => hasErrors(r) || hasWarnings(r)) || hasGuestWarning(v)) {
        setValidation(v)
      } else {
        await checkIn(v)
      }
    },
    [reservation, hotelDate, selectedReservedResources, checkIn],
  )

  return (
    <>
      <OperationIfGranted
        {...hicOperationsPermissions.get(HicOperations.CHECKOUT)}
        childrenWithoutPermissions={
          <Button variant="outlined" disabled={true} size="small">
            <FontAwesomeIcon icon={faPlaneArrival} style={{ marginRight: 2 }} />
            <span style={{ marginLeft: 2 }}>{t('pms.booking.reservation.action.check_in')}</span>
          </Button>
        }
      >
        <Button variant="outlined" disabled={(!isTimeResource ? reservation.checkInDisabled : !selectedRRAreTimeResourceType) || readOnly} size="small" onClick={() => onClick(context)}>
          <FontAwesomeIcon icon={faPlaneArrival} style={{ marginRight: 2 }} />
          <span style={{ marginLeft: 2 }}>{t('pms.booking.reservation.action.check_in')}</span>
        </Button>
      </OperationIfGranted>

      {validation != null && (
        <CheckInDialog //
          open={validation != null}
          validation={validation}
          reservation={reservation}
          onClose={() => setValidation(null)}
          checkIn={checkIn}
        />
      )}
    </>
  )
}

type CheckInDialogProps = {
  open: boolean
  onClose: () => void
  validation: ReservationCheckInValidation
  reservation: ReservationF
  checkIn: (v: ReservationCheckInValidation) => Promise<void>
}

function CheckInDialog({ open, onClose, validation, reservation, checkIn }: CheckInDialogProps) {
  const { t } = useTranslation()
  const localizeString = useLocalizeString()
  const theme = useTheme()
  const hotelDate = useHotelDate()
  const formatDate = useFormatDate()

  const onClick = useCallback(() => {
    checkIn(validation)
    onClose()
  }, [checkIn, validation])

  if (validation.error.noResources) {
    return (
      <Dialog open={open} onClose={onClose}>
        <DialogTitle>{t('pms.booking.reservation.check_in.dialog_title')}</DialogTitle>

        <DialogContent>
          <DialogContentText>{t('pms.booking.reservation.check_in.dialog_message.no_resource')}</DialogContentText>
        </DialogContent>

        <Divider light />

        <DialogActions>
          <Button variant="outlined" onClick={onClose}>
            {t('commons.cancel')}
          </Button>
        </DialogActions>
      </Dialog>
    )
  } else {
    return (
      <Dialog open={open} onClose={onClose}>
        <DialogTitle style={{ marginBottom: 0, paddingBottom: 0 }}>{t('pms.booking.reservation.check_in.dialog_title')}</DialogTitle>

        <DialogContent>
          <Grid container spacing={1}>
            {validation.reservedResources?.map((rr, i) => (
              <Grid key={rr.reservedResourceId} item xs={12} container spacing={1}>
                <Grid item xs={12} container spacing={1} alignItems="center">
                  <Grid item xs="auto">
                    {t('pms.booking.reservation.check_in.dialog_message.resource', { index: i + 1 })}
                  </Grid>
                  <Grid item xs>
                    <strong>{displayReservedResource(reservation, rr.reservedResourceId, localizeString)}</strong>
                  </Grid>
                  <Grid item xs="auto">
                    {hasErrors(rr) && ( //
                      <FontAwesomeIcon icon={faTriangleExclamation} color={theme.palette.error.main} />
                    )}
                    {hasWarnings(rr) && ( //
                      <FontAwesomeIcon icon={faTriangleExclamation} color={theme.palette.warning.main} />
                    )}
                    {!hasErrors(rr) &&
                      !hasWarnings(rr) && ( //
                        <FontAwesomeIcon icon={faCircleCheck} color={theme.palette.success.main} />
                      )}
                  </Grid>
                </Grid>

                {(hasErrors(rr) || hasWarnings(rr)) && ( //
                  <Grid item xs={12} mt={1}>
                    {hasErrors(rr) && ( //
                      <div>
                        <Typography variant="body">
                          <b>{t('pms.booking.reservation.check_in.dialog_message.cannot_check_in_resource')}</b>
                        </Typography>
                      </div>
                    )}
                    <ul style={{ marginTop: 0, marginBottom: 0 }}>
                      {hasErrors(rr) &&
                        rr.error.missingResource && ( //
                          <li>
                            <div>
                              <Typography variant="body">{t('pms.booking.reservation.check_in.dialog_message.missing_resource')}</Typography>
                            </div>
                          </li>
                        )}
                      {hasErrors(rr) &&
                        (rr.error.checkoutDateExpired || rr.error.checkinDateExpired) && ( //
                          <li>
                            <div>
                              <Typography variant="body">{t('pms.booking.reservation.check_in.dialog_message.wrong_dates', { date: formatDate(hotelDate) })}</Typography>
                            </div>
                          </li>
                        )}
                      {hasErrors(rr) &&
                        rr.error.dataHasChanged && ( //
                          <li>
                            <div>
                              <Typography variant="body">{t('pms.booking.reservation.check_in.dialog_message.data_has_changes')}</Typography>
                            </div>
                          </li>
                        )}
                      {hasErrors(rr) &&
                        rr.error.checkingInAlreadyCheckedInResourceWithNoGuests && ( //
                          <li>
                            <div>
                              <Typography variant="body">{t('pms.booking.reservation.check_in.dialog_message.checking_in_already_checked_in_resource_with_no_guests')}</Typography>
                            </div>
                          </li>
                        )}
                      {hasWarnings(rr) &&
                        rr.warning.pastStartDate && ( //
                          <li>
                            <div>
                              <Typography variant="body">{t('pms.booking.reservation.check_in.dialog_message.past_start_date')}</Typography>
                            </div>
                          </li>
                        )}
                      {hasWarnings(rr) &&
                        rr.warning.partialCheckIn && ( //
                          <li>
                            <div>
                              <Typography variant="body">{t('pms.booking.reservation.check_in.dialog_message.partial_check_in')}</Typography>
                            </div>
                          </li>
                        )}
                      {hasWarnings(rr) &&
                        rr.warning.emptyCheckIn && ( //
                          <li>
                            <div>
                              <Typography variant="body">{t('pms.booking.reservation.check_in.dialog_message.empty_check_in')}</Typography>
                            </div>
                          </li>
                        )}
                      {hasWarnings(rr) &&
                        rr.warning.wrongBirthDates && ( //
                          <li>
                            <div>
                              <Typography variant="body">{t('pms.booking.reservation.check_in.dialog_message.wrong_birth_dates')}</Typography>
                            </div>
                          </li>
                        )}
                    </ul>
                  </Grid>
                )}

                <Grid item xs={12}>
                  <Divider light />
                </Grid>
              </Grid>
            ))}

            {hasGuestWarning(validation) && ( //
              <Grid item xs={12}>
                <Typography variant="body">{t('pms.booking.reservation.check_in.dialog_message.partial_check_in_summary')}</Typography>
              </Grid>
            )}
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button variant="outlined" onClick={onClose}>
            {t('commons.cancel')}
          </Button>

          {showCheckInButton(validation) && ( //
            <Button variant="contained" onClick={onClick}>
              {t('pms.booking.reservation.check_in.dialog_action.check_in')}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    )
  }
}

function displayReservedResource(reservation: ReservationF, reservedResourceId: string, localize: (str: LocalizedString | null | undefined) => string) {
  const resource = reservation.reservedResources?.find((rr) => rr.id === reservedResourceId)?.currentResourceOccupancy?.resource?.code
  const type = localize(reservation.reservedResources?.find((rr) => rr.id === reservedResourceId)?.currentResourceOccupancy?.reservedResourceType?.name?.description)
  const strs: string[] = []
  if (type) {
    strs.push(type)
  }
  if (resource) {
    strs.push(resource)
  }
  return strs.join(' - ')
}

function hasErrors(validation: ReservedResourceCheckInValidation) {
  return (
    validation.error.missingResource ||
    validation.error.checkoutDateExpired ||
    validation.error.checkinDateExpired ||
    validation.error.dataHasChanged ||
    validation.error.checkingInAlreadyCheckedInResourceWithNoGuests
  )
}

function hasWarnings(validation: ReservedResourceCheckInValidation) {
  return !hasErrors(validation) && (validation.warning.pastStartDate || validation.warning.emptyCheckIn || validation.warning.partialCheckIn || validation.warning.wrongBirthDates)
}

function hasGuestWarning(validation: ReservationCheckInValidation) {
  return validation.reservedResources.some((rr) => rr.warning.emptyCheckIn || rr.warning.partialCheckIn)
}

function showCheckInButton(validation: ReservationCheckInValidation) {
  return validation.reservedResources.some((rr) => !hasErrors(rr))
}
